'use client';

import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { ContactFormWebApiData } from './ContactForm.types';
import { stack } from '@/uiPrimitives/layout/stack';
import { h2CSS, h3CSS } from '@/uiComposites/typography/headings';
import { textBlock } from '@/uiPrimitives/typography/text';
import { arrange } from '@/uiPrimitives/layout/arrange';
import { useContactForm } from './useContactForm';
import {
	callToActionBox,
	secondaryButtonBox,
} from '@/uiComposites/interactive/callToAction';
import { translateInline } from '@/globals/translate/translateInline';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { useFormStatus } from 'react-dom';
import { placement } from '@/uiPrimitives/layout/placement.css';

export const PreviewBlock = ({
	form,
}: {
	form: ContactFormWebApiData['form'];
}) => {
	const [contactFormState, dispatch] = useContactForm();
	const { pending } = useFormStatus();

	if (contactFormState == null || !contactFormState.previewMode) return null;

	return (
		<div
			className={classes(
				box({
					backgroundColor: 'negativeBlue',
					paddingBlockStart: '4 | betweenListItems',
					paddingInline: '4 | betweenListItems',
				}),
				stack({ rowGap: '4 | betweenListItems' }),
			)}
			{...addTestSelector('uiPreviewBlock')}
		>
			<div className={classes(stack({ rowGap: '6 | betweenH4Sections' }))}>
				<div className={classes(stack({ rowGap: '5 | betweenParagraphs' }))}>
					<h2 className={classes(h2CSS)}>
						{translateInline({
							en: 'Check your message and contact details',
							nl: 'Controleer uw gegevens',
						})}
					</h2>
					<p className={classes(textBlock({}))}>
						{translateInline({
							en: 'Here is your input for the form. You can now submit this form. In case you have made a mistake, you can edit your input.',
							nl: 'Hieronder staan uw ingevulde gegevens. U kunt uw gegevens nu versturen. Heeft u een fout gemaakt? Dan kunt u uw gegevens nu nog veranderen.',
						})}
					</p>
				</div>
				{form.fieldsets.map(({ legend, fields }, fieldsetIndex) => (
					<div
						key={fieldsetIndex}
						className={classes(stack({ gap: '6 | betweenH4Sections' }))}
					>
						{legend && <h3 className={h3CSS}>{legend}</h3>}
						<ul className={classes(stack({ rowGap: '6 | betweenH4Sections' }))}>
							{fields.map(({ name, ...fieldProps }, fieldIndex) => {
								if (!('label' in fieldProps)) return null;

								const value = contactFormState.fields[name].value;

								return (
									<li
										key={`preview-${fieldsetIndex}-${fieldIndex}`}
										className={classes(
											arrange({
												layout: { max640px: 'stack', min640px: 'shelf' },
												gap: '2 | h2',
												alignItems: 'start',
												gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
											}),
										)}
									>
										<p
											className={classes(
												textBlock({
													fontWeight: 'bold',
													textAlign: { max640px: 'start', min640px: 'end' },
												}),
												placement({ justifySelf: 'end' }),
											)}
										>
											{fieldProps.label}
										</p>
										{value ? (
											<p
												className={classes(
													textBlock({
														whiteSpace: 'pre-wrap',
														overflowWrap: 'anywhere',
													}),
												)}
											>
												{value}
											</p>
										) : (
											<p
												className={classes(
													textBlock({
														color: 'positiveMuted',
														fontStyle: 'italic',
													}),
												)}
											>
												{translateInline({ en: 'empty', nl: 'leeg' })}
											</p>
										)}
									</li>
								);
							})}
						</ul>
					</div>
				))}
			</div>
			{pending ? (
				<p
					className={classes(
						textBlock({ color: 'positiveMuted' }),
						box({
							paddingBlockEnd: {
								max640px: '0px',
								min640px: '4 | betweenListItems',
							},
						}),
					)}
				>
					{translateInline({
						en: 'Your message will be sent',
						nl: 'Uw bericht wordt verstuurd',
					})}
				</p>
			) : (
				<div
					className={classes(
						arrange({
							layout: { max640px: 'stack', min640px: 'shelf' },
							gap: '2 | h2',
							gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
						}),
						box({ inlineSize: { max640px: 'fit-content', min640px: 'auto' } }),
					)}
				>
					<button
						type="button"
						{...addTestSelector('uiContactFormEditBtn')}
						className={classes(
							secondaryButtonBox({
								box: {
									inlineSize: { max640px: 'auto', min640px: 'fit-content' },
								},
							}),
							placement({ justifySelf: 'end' }),
						)}
						onClick={() => {
							dispatch({ type: 'escapePreviewMode' });
						}}
					>
						<span className={classes(textBlock({}))}>
							{translateInline({
								en: 'Change your input',
								nl: 'Verander uw gegevens',
							})}
						</span>
					</button>

					<button
						type="submit"
						{...addTestSelector('uiSubmitBtn')}
						className={classes(
							callToActionBox({
								box: {
									inlineSize: { max640px: 'auto', min640px: 'fit-content' },
								},
							}),
						)}
					>
						<span className={classes(textBlock({}))}>
							{translateInline({
								en: 'Send your message',
								nl: 'Verstuur uw melding',
							})}
						</span>
					</button>
				</div>
			)}
		</div>
	);
};
